<template>

  <div class="sd_forms">
    <b-row class="content-header">
      <b-col
        class="content-header-left mb-2"
        cols="12"
        md="12"
      >
        <b-row class="breadcrumbs-top">
          <b-col cols="12">
            <h1 class="custom-header-title">
              Set Up Service Description
            </h1>
            <p
              v-if="sourceCreatedAt"
              class="log_info"
            >
              Created by {{ sourceCreatedBy }} on {{ sourceCreatedAt }} <br>Last updated {{ sourceUpdatedAt }}
            </p>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-form @submit.prevent="submitForm">
      <validation-observer
        ref="serviceDescriptionSetupForm"
        #default="{invalid}"
      >
        <b-card>
          <b-row>
            <b-col cols="12">
              <validation-provider
                #default="{ errors }"
                name="Title"
                vid="title"
                rules="required"
              >
                <b-form-group
                  label="Title*"
                  label-for="setup-title"
                  label-cols-md="3"
                >
                  <b-form-input
                    id="setup-title"
                    v-model="title"
                    placeholder="Title"
                    class="field-narrow"
                    :state="(errors.length > 0 || titleValidation) ? false : null"
                    @input="titleValidation = false"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="titleValidation"
                    class="text-danger"
                  >{{ titleValidationMessage }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12">
              <b-form-group
                label="Description"
                label-for="setup-description"
                label-cols-md="3"
              >
                <b-form-textarea
                  id="setup-description"
                  v-model="description"
                  placeholder="Internal description"
                  rows="3"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <b-form-group
                label="Applicable Appointment Types"
                label-cols-md="3"
              >
                <checkbox-tag-dropdown
                  v-model="applicableAppointmentTypes"
                  :options="appointmentTypeCheckboxOptions"
                  all-label="All Appointments"
                  placeholder="Select from list"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group
                label="Applicable Service Types"
                label-cols-md="3"
              >
                <checkbox-tag-dropdown
                  v-model="applicableServiceTypes"
                  :options="serviceTypeCheckboxOptions"
                  all-label="All Service Types"
                  placeholder="Select from list"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <validation-provider
                #default="{ errors }"
                name="Status"
                vid="status"
                rules="required"
              >
                <b-form-group
                  label="Status*"
                  label-for="setup-status"
                  label-cols-md="3"
                >
                  <v-select
                    id="setup-status"
                    v-model="status"
                    :options="statusOptions"
                    :clearable="false"
                    class="field-narrow"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>
        </b-card>

        <b-card
          header-tag="header"
        >
          <template #header>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="button"
              variant="flat-primary"
              class="primary-button-with-background mr-3"
            >
              <feather-icon
                icon="GridIcon"
                size="20"
              />
            </b-button>
            <h3 class="align-middle mr-auto">
              Service Description Items
            </h3>
          </template>

          <b-row
            v-for="(field, index) in items"
            :key="field.id"
          >
            <b-col cols="12">
              <b-form-group
                label="Parent"
                :label-for="'sdi-parent' + index"
                label-cols-md="3"
              >
                <v-select
                  :id="'sdi-parent' + index"
                  v-model="field.parent"
                  label="label"
                  placeholder="Select from list"
                  class="field-narrow"
                  :options="parentComponents"
                  :reduce="el => el.id"
                  :selectable="option => option.id != field.id"
                  :clearable="true"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <validation-provider
                #default="{ errors }"
                name="Component Type"
                :vid="'component-type-' + index"
                rules="required"
              >
                <b-form-group
                  label="Component Type*"
                  :label-for="'sdi-type' + index"
                  label-cols-md="3"
                  :state="(errors.length > 0) ? false : null"
                >
                  <v-select
                    :id="'sdi-type' + index"
                    v-model="field.type"
                    label="title"
                    :options="inputTypeOptions"
                    :reduce="title => title.code"
                    :clearable="false"
                    class="field-narrow"
                    @input="updateExtraFields(index)"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col
              v-if="field.type == 'file'"
              cols="12"
            >
              <b-form-group
                label="Upload Image*"
                :label-for="'sdi-file' + index"
                label-cols-md="3"
              >
                <b-form-file
                  :id="'sdi-file' + index"
                  v-model="field.file"
                  accept="image/*"
                  @input="setImage(index)"
                />
              </b-form-group>
            </b-col>
            <b-col
              v-if="field.type == 'file'"
              cols="12"
            >
              <b-img
                thumbnail
                fluid
                width="200px"
                height="auto"
                :src="field.base64File"
              />
            </b-col>

            <!-- Title and Text -->
            <b-col cols="12">
              <div
                v-if="field.type == 'title-and-text'"
                class="row"
              >
                <b-col cols="12">
                  <validation-provider
                    #default="{ errors }"
                    name="Title"
                    :vid="'title-' + index"
                    rules="required"
                  >
                    <b-form-group
                      label="Title*"
                      :label-for="'sdi-label' + index"
                      label-cols-md="3"
                    >
                      <b-form-input
                        :id="'sdi-label' + index"
                        v-model="field.label"
                        placeholder="Title"
                        class="field-medium"
                        :state="(errors.length > 0) ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col cols="12">
                  <b-form-group
                    label="Text"
                    :label-for="'sdi-description' + index"
                    label-cols-md="3"
                  >
                    <b-form-textarea
                      :id="'sdi-description' + index"
                      v-model="field.description"
                      placeholder="Text"
                      rows="3"
                    />
                  </b-form-group>
                </b-col>
              </div>
            </b-col>

            <!-- Label / Base Label / Description shared by all other field types -->
            <b-col cols="12">
              <div
                v-if="field.type == 'short-answer' || field.type == 'increment' || field.type == 'long-answer' || field.type == 'multi-select' || field.type == 'dropdown' || field.type == 'single-select'"
                class="row"
              >
                <b-col cols="12">
                  <validation-provider
                    #default="{ errors }"
                    name="Label"
                    :vid="'label-' + index"
                    rules="required"
                  >
                    <b-form-group
                      label="Label*"
                      :label-for="'sdi-label' + index"
                      label-cols-md="3"
                    >
                      <b-form-input
                        :id="'sdi-label' + index"
                        v-model="field.label"
                        placeholder="Field Label"
                        class="field-medium"
                        :state="(errors.length > 0) ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col cols="12">
                  <b-form-group
                    label="Base Label"
                    :label-for="'sdi-base-label' + index"
                    label-cols-md="3"
                  >
                    <b-form-input
                      :id="'sdi-base-label' + index"
                      v-model="field.baseLabel"
                      placeholder="Field Base Label"
                      class="field-medium"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group
                    label="Description"
                    :label-for="'sdi-field-description' + index"
                    label-cols-md="3"
                  >
                    <b-form-textarea
                      :id="'sdi-field-description' + index"
                      v-model="field.description"
                      placeholder="Description"
                      rows="3"
                    />
                  </b-form-group>
                </b-col>

                <!-- Quantity: explicit min/max/increments -->
                <template v-if="field.type == 'increment'">
                  <b-col cols="12">
                    <validation-provider
                      #default="{ errors }"
                      name="Min Quantity"
                      :vid="'min-quantity-' + index"
                      rules="required"
                    >
                      <b-form-group
                        label="Min Quantity*"
                        :label-for="'sdi-min-qty' + index"
                        label-cols-md="3"
                      >
                        <b-form-input
                          :id="'sdi-min-qty' + index"
                          v-model="field.minQuantity"
                          type="number"
                          placeholder="0"
                          class="field-narrow"
                          :state="(errors.length > 0) ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                  <b-col cols="12">
                    <validation-provider
                      #default="{ errors }"
                      name="Max Quantity"
                      :vid="'max-quantity-' + index"
                      rules="required"
                    >
                      <b-form-group
                        label="Max Quantity*"
                        :label-for="'sdi-max-qty' + index"
                        label-cols-md="3"
                      >
                        <b-form-input
                          :id="'sdi-max-qty' + index"
                          v-model="field.maxQuantity"
                          type="number"
                          placeholder="0"
                          class="field-narrow"
                          :state="(errors.length > 0) ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                  <b-col cols="12">
                    <validation-provider
                      #default="{ errors }"
                      name="Increments"
                      :vid="'increments-' + index"
                      rules="required"
                    >
                      <b-form-group
                        label="Increments*"
                        :label-for="'sdi-increments' + index"
                        label-cols-md="3"
                      >
                        <b-form-input
                          :id="'sdi-increments' + index"
                          v-model="field.increments"
                          type="number"
                          placeholder="1"
                          class="field-narrow"
                          :state="(errors.length > 0) ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </template>

                <!-- Tag Product: Quantity + Single Checkbox Field -->
                <b-col
                  v-if="field.type == 'increment' || field.type == 'single-select'"
                  cols="12"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Tag Product"
                    :vid="'tag-product-' + index"
                    rules="required"
                  >
                    <b-form-group
                      label="Tag Product*"
                      :label-for="'sdi-tag-product' + index"
                      label-cols-md="3"
                    >
                      <div class="tag-product-field field-medium">
                        <v-select
                          :id="'sdi-tag-product' + index"
                          v-model="field.tagProduct"
                          label="name"
                          placeholder="Search item"
                          :options="tagProductOptions"
                          :reduce="item => item._id"
                          class="tag-product-select"
                          :class="{ 'is-invalid': errors.length > 0 }"
                        />
                        <b-img
                          v-if="tagProductImage(field.tagProduct)"
                          rounded="circle"
                          width="36"
                          height="36"
                          :src="tagProductImage(field.tagProduct)"
                          class="tag-product-thumb"
                        />
                      </div>
                      <small class="text-danger">{{ errors[0] }}</small>
                      <small class="text-muted">Tag this SD item to a Catalog Product, to allow users to add to cart by selecting a quantity when a Customer or Agent are submitting a Service Request (via a Service Form) or when a staff is editing an existing Service Request.</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <!-- Selection List Field / Dropdown Field options -->
                <b-col
                  v-if="field.type == 'multi-select' || field.type == 'dropdown'"
                  cols="12"
                >
                  <b-form-group
                    label="Add Selection Option*"
                    label-cols-md="3"
                  >
                    <div
                      v-for="(opt, key) in field.extraOptions"
                      :key="key"
                      class="selection-option-row mb-2"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Option Name"
                        :vid="'option-' + index + '-' + key"
                        rules="required"
                      >
                        <b-form-input
                          v-model="opt.key"
                          placeholder="Type an option name"
                          :state="(errors.length > 0) ? false : null"
                          class="mr-1 mb-1"
                        />
                      </validation-provider>
                      <b-form-input
                        v-model="opt.value"
                        placeholder="Text to display in calendar"
                        class="mr-1 mb-1"
                      />
                      <validation-provider
                        #default="{ errors }"
                        name="Tag Product"
                        :vid="'option-tag-product-' + index + '-' + key"
                        rules="required"
                        class="mr-1 mb-1"
                      >
                        <div class="tag-product-field">
                          <v-select
                            v-model="opt.tagProduct"
                            label="name"
                            placeholder="Search item"
                            :options="tagProductOptions"
                            :reduce="item => item._id"
                            class="option-tag-product"
                            :class="{ 'is-invalid': errors.length > 0 }"
                          />
                          <b-img
                            v-if="tagProductImage(opt.tagProduct)"
                            rounded="circle"
                            width="30"
                            height="30"
                            :src="tagProductImage(opt.tagProduct)"
                            class="tag-product-thumb"
                          />
                        </div>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                      <b-form-checkbox
                        v-model="opt.showInFrontend"
                        value="yes"
                        unchecked-value="no"
                        class="mr-1 mb-1"
                      >
                        Visible to customer/ agent
                      </b-form-checkbox>
                      <b-button
                        variant="flat-secondary"
                        type="button"
                        class="btn-icon rounded-circle mb-1"
                        @click="removeOption(index, key)"
                      >
                        <feather-icon
                          icon="Trash2Icon"
                          size="18"
                        />
                      </b-button>
                    </div>
                    <p
                      class="addOptionText"
                      @click="addOption(index)"
                    >
                      Add an option
                    </p>
                  </b-form-group>
                </b-col>

                <b-col
                  v-if="field.type == 'multi-select'"
                  cols="12"
                >
                  <b-form-group
                    label="User Can Select*"
                    label-cols-md="3"
                  >
                    <b-form-radio-group
                      v-model="field.multiSelectType"
                      button-variant="outline-primary"
                      buttons
                      class="spaceBetweenRadio"
                    >
                      <b-form-radio
                        v-for="(option, kindex) in multiSelectOptions"
                        :key="kindex"
                        :value="option.value"
                      >
                        <feather-icon
                          v-if="field.multiSelectType === option.value"
                          icon="CheckIcon"
                          class="mr-50"
                          size="16"
                        />
                        <span class="align-middle">{{ option.text }}</span>
                      </b-form-radio>
                    </b-form-radio-group>
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group
                    label="Required?*"
                    label-cols-md="3"
                  >
                    <b-form-radio-group
                      v-model="field.required"
                      button-variant="outline-primary"
                      buttons
                      class="spaceBetweenRadio"
                    >
                      <b-form-radio
                        v-for="(option, kindex) in yesNoOptions"
                        :key="kindex"
                        :value="option.value"
                      >
                        <feather-icon
                          v-if="field.required === option.value"
                          icon="CheckIcon"
                          class="mr-50"
                          size="16"
                        />
                        <span class="align-middle">{{ option.text }}</span>
                      </b-form-radio>
                    </b-form-radio-group>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group
                    label="Kitchen Item?*"
                    label-cols-md="3"
                  >
                    <b-form-radio-group
                      v-model="field.kitchenItem"
                      button-variant="outline-primary"
                      buttons
                      class="spaceBetweenRadio"
                    >
                      <b-form-radio
                        v-for="(option, kindex) in yesNoOptions"
                        :key="kindex"
                        :value="option.value"
                      >
                        <feather-icon
                          v-if="field.kitchenItem === option.value"
                          icon="CheckIcon"
                          class="mr-50"
                          size="16"
                        />
                        <span class="align-middle">{{ option.text }}</span>
                      </b-form-radio>
                    </b-form-radio-group>
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group
                    label="Visible to customer/ agents?*"
                    label-cols-md="3"
                  >
                    <b-form-radio-group
                      v-model="field.showInFrontend"
                      button-variant="outline-primary"
                      buttons
                      class="spaceBetweenRadio"
                    >
                      <b-form-radio
                        v-for="(option, kindex) in yesNoOptions"
                        :key="kindex"
                        :value="option.value"
                      >
                        <feather-icon
                          v-if="field.showInFrontend === option.value"
                          icon="CheckIcon"
                          class="mr-50"
                          size="16"
                        />
                        <span class="align-middle">{{ option.text }}</span>
                      </b-form-radio>
                    </b-form-radio-group>
                  </b-form-group>
                </b-col>
              </div>
            </b-col>

            <b-col cols="12">
              <p
                class="removeText"
                @click="removeField(index)"
              >
                Remove this component
              </p>
              <hr class="dividerHR">
            </b-col>
          </b-row>

          <b-button
            v-if="items.length > 0"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            type="button"
            variant="primary"
            @click="addField"
          >
            <span class="align-middle">Add custom field</span>
          </b-button>
          <b-row v-else>
            <b-col>
              <div class="empty_block">
                <h4>No item yet</h4>
                <b-button
                  type="button"
                  variant="flat-primary"
                  @click="addField"
                >
                  <span class="align-middle">Add custom field</span>
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-card>

        <b-nav class="wrap-border save-nav">
          <li class="nav-item ml-auto">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="button"
              variant="flat-primary"
              @click="cancelEdit"
            >
              Cancel
            </b-button>
          </li>
          <li class="nav-item mr-1">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="success"
              :disabled="invalid"
            >
              <feather-icon
                icon="SaveIcon"
                class="mr-50"
              />
              <span class="align-middle">Save changes</span>
            </b-button>
          </li>
        </b-nav>
      </validation-observer>
    </b-form>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BCard, BRow, BCol, BFormGroup, BFormInput, BFormTextarea, BFormRadioGroup, BForm, BButton, BNav, BFormFile, BImg,
  BFormRadio, BFormCheckbox,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import { v4 as uuidv4 } from 'uuid'
import { required } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import CheckboxTagDropdown from './CheckboxTagDropdown.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormFile,
    BFormTextarea,
    BFormRadioGroup,
    BForm,
    BButton,
    BNav,
    BImg,
    BFormRadio,
    BFormCheckbox,
    vSelect,
    CheckboxTagDropdown,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      isEdit: false,
      entityId: null,
      sourceCreatedBy: '',
      sourceCreatedAt: '',
      sourceUpdatedAt: '',

      title: '',
      titleValidation: false,
      titleValidationMessage: '',
      description: '',
      applicableAppointmentTypes: [],
      applicableServiceTypes: [],
      status: 'Active',

      appointmentTypeOptions: [],
      serviceTypeOptions: [],
      statusOptions: ['Active', 'Inactive'],
      tagProductOptions: [],

      inputTypeOptions: [
        { title: 'Image', code: 'file' },
        { title: 'Title and Text', code: 'title-and-text' },
        { title: 'Quantity', code: 'increment' },
        { title: 'Short Answer Field', code: 'short-answer' },
        { title: 'Long Answer Field', code: 'long-answer' },
        { title: 'Selection List Field', code: 'multi-select' },
        { title: 'Dropdown Field', code: 'dropdown' },
        { title: 'Single Checkbox Field', code: 'single-select' },
      ],
      items: [],
      yesNoOptions: [
        { text: 'Yes', value: 'yes' },
        { text: 'No', value: 'no' },
      ],
      multiSelectOptions: [
        { text: 'One Option Only', value: 'single' },
        { text: 'Multiple Options', value: 'multiple' },
      ],

      required,
    }
  },
  computed: {
    parentComponents() {
      return this.items.filter(o => o.type === 'title-and-text' && o.label)
    },
    appointmentTypeCheckboxOptions() {
      return this.appointmentTypeOptions.map(type => ({ value: type._id, label: type.typeName }))
    },
    serviceTypeCheckboxOptions() {
      return this.serviceTypeOptions.map(type => ({ value: type._id, label: type.name }))
    },
  },
  created() {
    this.fetchAppointmentTypeOptions()
    this.fetchServiceTypeOptions()
    this.fetchTagProductOptions()

    if (this.$route.params.id) {
      this.isEdit = true
      this.entityId = this.$route.params.id
      this.$http.get(`operation/service-description-setups/${this.entityId}`)
        .then(response => {
          this.hydrate(response.data)
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    } else if (this.$route.query.from) {
      this.$http.get(`operation/service-description-setups/${this.$route.query.from}/show`)
        .then(response => {
          this.hydrate(response.data.data)
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }
  },
  methods: {
    hydrate(source) {
      this.title = source.title
      this.description = source.description
      this.applicableAppointmentTypes = (source.applicableAppointmentTypes || []).map(type => (type._id ? type._id : type))
      this.applicableServiceTypes = (source.applicableServiceTypes || []).map(type => (type._id ? type._id : type))
      this.status = source.status || 'Active'
      this.items = (source.items || []).map(field => ({
        ...field,
        id: field.id || uuidv4(),
        file: null,
        base64File: field.file || field.base64File || null,
        extraOptions: (field.extraOptions || []).map(opt => ({ ...opt })),
      }))

      if (this.isEdit) {
        this.sourceCreatedBy = source.createdBy ? source.createdBy.name : ''
        this.sourceCreatedAt = source.createdAt ? this.dateFormatWithTime(source.createdAt) : ''
        this.sourceUpdatedAt = source.updatedAt ? this.dateFormatWithTime(source.updatedAt) : ''
      }
    },
    fetchAppointmentTypeOptions() {
      this.$http.get('appointment/activity-types/respond-with/name-and-id', { params: { status: 'published' } })
        .then(response => {
          this.appointmentTypeOptions = response.data.activityTypes || []
        })
        .catch(() => {})
    },
    fetchServiceTypeOptions() {
      this.$http.get('operation/service-description-setups/respond-with/service-types')
        .then(response => {
          this.serviceTypeOptions = response.data.serviceTypes || []
        })
        .catch(() => {})
    },
    fetchTagProductOptions() {
      this.$http.get('products/retail-items/respond-with/name-and-id')
        .then(response => {
          this.tagProductOptions = response.data.stores || []
        })
        .catch(() => {})
    },
    tagProductImage(id) {
      const product = this.tagProductOptions.find(item => item._id === id)
      return product ? product.image : null
    },
    addField() {
      this.items.push({
        id: uuidv4(),
        parent: '',
        type: 'title-and-text',
        label: '',
        baseLabel: '',
        description: '',
        required: 'yes',
        kitchenItem: 'yes',
        showInFrontend: 'yes',
        extraOptions: [],
        file: null,
        base64File: null,
        multiSelectType: 'single',
        minQuantity: 0,
        maxQuantity: 10,
        increments: 1,
        tagProduct: null,
      })
    },
    updateExtraFields(index) {
      this.items[index].extraOptions = []
      if (this.items[index].type === 'multi-select' || this.items[index].type === 'dropdown') {
        this.items[index].extraOptions.push({
          key: '', value: '', showInFrontend: 'yes', kitchenItem: 'yes', tagProduct: null,
        })
      }
    },
    addOption(index) {
      this.items[index].extraOptions.push({
        key: '', value: '', showInFrontend: 'yes', kitchenItem: 'yes', tagProduct: null,
      })
    },
    removeOption(index, key) {
      if (this.items[index].extraOptions.length > 1) {
        this.items[index].extraOptions.splice(key, 1)
      } else {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'This has to have at least one option.',
            icon: 'BellIcon',
            variant: 'warning',
          },
        })
      }
    },
    removeField(index) {
      this.$swal({
        title: 'Are You Sure?',
        html: 'Selecting Remove will <strong>permanently delete this component / custom field</strong>. This action cannot be undone.',
        // eslint-disable-next-line global-require
        imageUrl: require('@/assets/images/icons/warning.png'),
        reverseButtons: true,
        showCancelButton: true,
        cancelButtonText: 'No, Cancel',
        confirmButtonText: 'Yes, Delete!',
        customClass: {
          confirmButton: 'btn btn-danger ml-1',
          cancelButton: 'btn btn-outline-primary',
        },
        buttonsStyling: false,
      })
        .then(result => {
          if (result.value) {
            this.items.splice(index, 1)
          }
        })
    },
    setImage(index) {
      const reader = new FileReader()
      reader.readAsDataURL(this.items[index].file)
      reader.onload = () => {
        this.items[index].base64File = reader.result
      }
    },
    cancelEdit() {
      this.$swal({
        title: 'Are you sure?',
        html: 'Selecting Cancel will <strong>undo any changes</strong> you have made. This action cannot be undone.',
        // eslint-disable-next-line global-require
        imageUrl: require('@/assets/images/icons/warning.png'),
        imageWidth: 80,
        imageHeight: 80,
        imageAlt: 'Custom Icon',
        reverseButtons: true,
        showCancelButton: true,
        confirmButtonText: 'Yes, Cancel!',
        cancelButtonText: 'No, Go Back',
        customClass: {
          confirmButton: 'btn btn-danger ml-1',
          cancelButton: 'btn btn-outline-primary',
        },
        buttonsStyling: false,
      })
        .then(result => {
          if (result.value) {
            this.$router.push({ name: 'operation-service-description-setups-index' })
          }
        })
    },
    submitForm() {
      this.$refs.serviceDescriptionSetupForm.validate().then(success => {
        if (!success) return

        const trimmedItems = this.items.map(field => ({
          id: field.id,
          parent: field.parent,
          type: field.type,
          file: field.base64File,
          label: field.label,
          baseLabel: field.baseLabel,
          description: field.description,
          required: field.required,
          kitchenItem: field.kitchenItem,
          showInFrontend: field.showInFrontend,
          multiSelectType: field.multiSelectType,
          minQuantity: field.minQuantity,
          maxQuantity: field.maxQuantity,
          increments: field.increments,
          tagProduct: field.tagProduct,
          extraOptions: (field.extraOptions || []).map(opt => ({
            key: opt.key,
            value: opt.value,
            showInFrontend: opt.showInFrontend,
            kitchenItem: opt.kitchenItem,
            tagProduct: opt.tagProduct,
          })),
        }))

        const formData = new FormData()
        formData.append('title', this.title)
        formData.append('description', this.description || '')
        formData.append('status', this.status)
        formData.append('applicableAppointmentTypes', JSON.stringify(this.applicableAppointmentTypes))
        formData.append('applicableServiceTypes', JSON.stringify(this.applicableServiceTypes))
        formData.append('items', JSON.stringify(trimmedItems))

        const request = this.isEdit
          ? this.$http.patch(`operation/service-description-setups/${this.entityId}/update`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
          : this.$http.post('operation/service-description-setups/store', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })

        request
          .then(response => {
            this.$swal({
              title: 'Changes have Saved',
              html: response.data.message || '',
              // eslint-disable-next-line global-require
              imageUrl: require('@/assets/images/icons/save.png'),
              imageWidth: 80,
              imageHeight: 80,
              imageAlt: 'Custom Icon',
              showCancelButton: false,
              allowOutsideClick: false,
              confirmButtonText: 'Okay',
              customClass: {
                confirmButton: 'btn btn-primary',
              },
              buttonsStyling: false,
            })
              .then(result => {
                if (result.value) {
                  this.$router.push({ name: 'operation-service-description-setups-index' })
                }
              })
          })
          .catch(error => {
            if (error.data.errors) {
              error.data.errors.forEach(validationError => {
                if (validationError.param === 'title') {
                  this.titleValidation = true
                  this.titleValidationMessage = validationError.msg
                }
              })
            }
            this.$toast({
              component: ToastificationContent,
              props: {
                title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          })
      })
    },
  },
}
</script>

<style scoped>
  .removeText {
    color:#D91B35;
    cursor: pointer;
  }
  .addOptionText {
    color: #104D9D;
    cursor: pointer;
  }
  .btn-group-toggle [class*=btn-outline-]:not(:last-child) {
    border-right-width: 1px !important;
  }
  .selection-option-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
  .selection-option-row .option-tag-product {
    min-width: 200px;
  }
  .field-narrow {
    max-width: 220px;
  }
  .field-medium {
    max-width: 420px;
  }
  .tag-product-field {
    display: flex;
    align-items: center;
    flex: 1;
  }
  .tag-product-field .v-select {
    flex: 1;
  }
  .tag-product-thumb {
    flex-shrink: 0;
    margin-left: 0.75rem;
    object-fit: cover;
  }
</style>
